/**
 * ClientDetailDrawer — Azure overview-blade style right-side flyout.
 *
 * Opens over the Clients list when a client row is clicked. URL reflects
 * the selection (/clients/:id) so deep-links and refresh work correctly.
 * Close via: X button, backdrop click, or Escape key.
 *
 * Widths:
 *   desktop  (>1024px) : min(80vw, 1280px)
 *   landscape tablet (≤1024px) : 90vw
 *   portrait tablet / mobile (≤768px) : 100vw
 *
 * Layout: single-scroll blade — sticky header + key-facts strip + actions toolbar,
 * then stacked sections: Details · Clinicians · Goals (no tabs).
 *
 * Nested overlays stacking: the drawer Panel sits at theme.zIndex.modal (1050).
 * Each nested overlay (EditClientModal, SetOverseeingBcbaModal, AssignProvidersPanel,
 * and the Add/Edit/Change-status/Delete/Import modals owned by ClientGoalsPanel) is
 * passed zIndex={theme.zIndex.popover} (1060) so it stacks above the drawer.
 * AssignProvidersPanel is position:fixed (not portaled) and accepts a zIndex prop;
 * Modal-based overlays portal into document.body and also accept a zIndex prop.
 * Escape in the drawer is suppressed via a ref while any nested overlay is open:
 * `activePanelRef` for EditClientModal/AssignProvidersPanel/SetOverseeingBcbaModal,
 * and `goalsOverlayOpenRef` (fed by ClientGoalsPanel's `onOverlayOpenChange`) for its
 * own Add/Edit/Change-status/Delete/Import modals, which it opens via local state the
 * drawer doesn't otherwise see. Without that second ref, Escape while a goal modal is
 * open would close the modal *and* bubble to close the drawer behind it.
 *
 * TODO (tech-debt): Overlay/Panel/slideIn/CloseBtn/Body are also duplicated in
 * AssignProvidersPanel and UserDetailSidebar. Extract into a shared
 * <SideDrawer> primitive in components/ui/ when refactoring those panels.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, useTheme } from 'styled-components';
import { X, Pencil, UserPlus, UserCog, ChevronDown, Check } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { media } from '../../styles/media';
import { useClientDetail, useUpdateClientStatus } from '../../hooks/useClients';
import { useCanSetClientInactive } from '../../hooks/useCanSetClientInactive';
import { extractApiError } from '../../utils/apiError';
import { fullName } from '../../utils/nameUtils';
import type { ClientStatus } from '../../types/clients';
import {
  CLIENT_STATUS_BADGE_VARIANT,
  CLIENT_STATUS_LABEL,
  clientStatusOptions,
} from '../../pages/ClientManagementPage/clientFormConstants';
import { EditClientModal } from '../../pages/ClientManagementPage/EditClientModal';
import { AssignProvidersPanel } from './AssignProvidersPanel';
import { SetOverseeingBcbaModal } from './SetOverseeingBcbaModal';
import { OverviewTab } from '../../pages/ClientDetailPage/OverviewTab';
import { CliniciansTab } from '../../pages/ClientDetailPage/CliniciansTab';
import { ClientGoalsPanel } from '../../pages/GoalsManagementPage/ClientGoalsPanel';
import { ClientStatusHistorySection } from './ClientStatusHistorySection';

// ==================== Types ====================

/** Which nested action overlay is currently open, or null for none. */
type ActivePanel = 'edit' | 'assign' | 'bcba' | null;

interface ClientDetailDrawerProps {
  clientId: string;
  onClose: () => void;
  /** When false, the mutating actions (Edit / Assign / Set BCBA) are hidden — mirrors
   *  the page's read-only lock so the drawer can't be used to bypass it. */
  canManage?: boolean;
}

// ==================== Helpers ====================


// ==================== Animations ====================

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

// ==================== Styled Components ====================

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(80vw, 1280px);
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;

  ${media.desktop} {
    width: 90vw;
  }

  ${media.tablet} {
    width: 100vw;
  }
`;

/**
 * Sticky header — name, status badge, close button, facts strip, actions bar.
 * Stays pinned while the body scrolls.
 */
const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  z-index: 1;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const TitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
  min-width: 0;
`;

const StatusMenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const StatusButton = styled.button<{ $variant: string; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.full ?? theme.borderRadius.lg};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  padding: 0;
  background: none;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

const StatusDropdownMenu = styled.div`
  position: fixed;
  min-width: 160px;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: ${({ theme }) => theme.zIndex.popover};
  overflow: hidden;
`;

const StatusMenuItem = styled.button<{ $current?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme, $current }) =>
    $current ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  background: none;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const DrawerTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

/** Compact key-facts strip — pipe-delimited inline chips */
const FactsStrip = styled.div`
  display: flex;
  align-items: stretch;
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  overflow-x: auto;

  ${media.tablet} {
    flex-wrap: wrap;
  }
`;

const Fact = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[5]};
  border-right: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  }
`;

const FactLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FactValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Actions toolbar — surfaced directly, no dropdown needed */
const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.page};
  flex-wrap: wrap;

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

/** Scrollable content body */
const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

/** Named section with a heading; subsequent sections get a top divider + spacing */
const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing[8]};
    padding-top: ${({ theme }) => theme.spacing[6]};
    border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const SectionHeading = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[16]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const StatusErrorNote = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[6]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.error.text};

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  }
`;

const NotFoundState = styled.div`
  padding: ${({ theme }) => theme.spacing[16]} ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

// ==================== Status dropdown ====================

interface StatusDropdownProps {
  status: ClientStatus;
  onSelect: (status: ClientStatus) => void;
  isPending: boolean;
  /** Offer the INACTIVE (deactivate) option — requires org-wide reach, since the
   *  API 403s a caseload-scoped caller who would lose all access to the record. */
  canSetInactive: boolean;
}

/** Status badge that opens a menu of the four statuses — this is the drawer's only
 *  path to reactivate an off-roster (INACTIVE) client, so it stays enabled for any
 *  status the client is currently in. */
function StatusDropdown({ status, onSelect, isPending, canSetInactive }: StatusDropdownProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const computePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.left });
  }, []);

  useEffect(() => {
    if (!open) return;
    computePosition();
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onReposition = () => setOpen(false);
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, computePosition]);

  return (
    <StatusMenuWrapper ref={wrapperRef}>
      <StatusButton
        ref={buttonRef}
        type="button"
        $variant={CLIENT_STATUS_BADGE_VARIANT[status]}
        $disabled={isPending}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Change client status, currently ${CLIENT_STATUS_LABEL[status]}`}
      >
        <Badge variant={CLIENT_STATUS_BADGE_VARIANT[status]}>
          {isPending ? 'Updating…' : CLIENT_STATUS_LABEL[status]}
        </Badge>
        <ChevronDown />
      </StatusButton>
      {open &&
        coords &&
        createPortal(
          <StatusDropdownMenu
            ref={menuRef}
            role="menu"
            style={{ top: coords.top, left: coords.left }}
          >
            {clientStatusOptions(canSetInactive).map(({ value: option }) => (
              <StatusMenuItem
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={option === status}
                $current={option === status}
                disabled={isPending}
                onClick={() => {
                  setOpen(false);
                  if (option !== status) onSelect(option);
                }}
              >
                {CLIENT_STATUS_LABEL[option]}
                {option === status && <Check />}
              </StatusMenuItem>
            ))}
          </StatusDropdownMenu>,
          document.body,
        )}
    </StatusMenuWrapper>
  );
}

// ==================== Component ====================

export function ClientDetailDrawer({
  clientId,
  onClose,
  canManage = false,
}: ClientDetailDrawerProps) {
  const theme = useTheme();
  // z-index for nested overlays — must exceed the drawer Panel (modal=1050)
  const nestedZIndex = theme.zIndex.popover; // 1060

  // Deactivating (→ INACTIVE) requires org-wide reach — only offered to callers the
  // server would accept.
  const canSetInactive = useCanSetClientInactive();

  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const activePanelRef = useRef<ActivePanel>(null);
  activePanelRef.current = activePanel;

  // ClientGoalsPanel owns its own Add/Edit/Change-status/Delete/Import modals rather
  // than routing through `activePanel` — it reports open/closed via this ref instead,
  // so Escape closes a goal modal without also closing the drawer behind it.
  const goalsOverlayOpenRef = useRef(false);
  const handleGoalsOverlayOpenChange = useCallback((isOpen: boolean) => {
    goalsOverlayOpenRef.current = isOpen;
  }, []);

  const { data: client, isLoading } = useClientDetail(clientId);

  const [statusError, setStatusError] = useState<string | null>(null);
  const updateStatusMutation = useUpdateClientStatus();
  const handleStatusSelect = (status: ClientStatus) => {
    if (!client) return;
    setStatusError(null);
    updateStatusMutation.mutate(
      { id: client.id, status },
      { onError: (err) => setStatusError(extractApiError(err, 'Failed to update status.')) },
    );
  };

  // Mirror the API's effective-status rule (client-visibility.util.ts): an
  // off-roster (inactive) client is read-only no matter how the drawer was
  // reached — filtered list or a direct /clients/:id link — since every write
  // endpoint rejects it. `status` is the sole source of truth for roster
  // membership; INACTIVE is the only off-roster status (ADO #538 AC8).
  // Derive from the loaded client, not the list filter.
  const clientOnRoster = !!client && client.status !== 'INACTIVE';
  const allowManage = canManage && clientOnRoster;

  const closePanel = () => setActivePanel(null);

  // Close on Escape — read active panel via ref so the listener is stable for the
  // drawer's lifetime and is not removed/re-added on each open/close toggle.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activePanelRef.current === null && !goalsOverlayOpenRef.current) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Lock body scroll while drawer is open (prevents page jump behind it)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const clientDisplayName = fullName(client) ?? 'Client';
  const supervisingBcba = fullName(client?.supervisingClinician) ?? '—';

  return (
    <>
      <Overlay onClick={onClose}>
        <Panel
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Client detail: ${clientDisplayName}`}
        >
          {/* ---- Sticky Header ---- */}
          <StickyHeader>
            <HeaderRow>
              <TitleArea>
                <DrawerTitle>{clientDisplayName}</DrawerTitle>
                {client &&
                  // Bare `canManage`, not `allowManage` — status change must work on an
                  // off-roster (INACTIVE) client, since that IS the reactivation path.
                  (canManage ? (
                    <StatusDropdown
                      status={client.status}
                      onSelect={handleStatusSelect}
                      isPending={updateStatusMutation.isPending}
                      canSetInactive={canSetInactive}
                    />
                  ) : (
                    <Badge variant={CLIENT_STATUS_BADGE_VARIANT[client.status]}>
                      {CLIENT_STATUS_LABEL[client.status]}
                    </Badge>
                  ))}
              </TitleArea>
              <CloseBtn onClick={onClose} aria-label="Close client detail">
                <X />
              </CloseBtn>
            </HeaderRow>

            {client && (
              <>
                {/* Key-facts strip — Center · Assigned BCBA · Clinicians */}
                <FactsStrip>
                  <Fact>
                    <FactLabel>Center</FactLabel>
                    <FactValue>{client.primaryCenter?.name ?? '—'}</FactValue>
                  </Fact>
                  <Fact>
                    <FactLabel>Assigned BCBA</FactLabel>
                    <FactValue>{supervisingBcba}</FactValue>
                  </Fact>
                  <Fact>
                    <FactLabel>Clinicians</FactLabel>
                    <FactValue>{client.clinicianCount ?? 0}</FactValue>
                  </Fact>
                </FactsStrip>

                {statusError && <StatusErrorNote>{statusError}</StatusErrorNote>}

                {/* Actions toolbar — hidden in read-only mode (mirrors the page guard) */}
                {allowManage && (
                  <ActionsBar>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Pencil size={14} />}
                      onClick={() => setActivePanel('edit')}
                    >
                      Edit Client
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<UserPlus size={14} />}
                      onClick={() => setActivePanel('assign')}
                    >
                      Assign Providers
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<UserCog size={14} />}
                      onClick={() => setActivePanel('bcba')}
                    >
                      {client.supervisingClinicianId ? 'Replace BCBA' : 'Set BCBA'}
                    </Button>
                  </ActionsBar>
                )}
              </>
            )}
          </StickyHeader>

          {/* ---- Body ---- */}
          <Body>
            {isLoading && (
              <LoadingState>
                <LoadingSpinner size="md" />
                Loading client…
              </LoadingState>
            )}

            {!isLoading && !client && <NotFoundState>Client not found.</NotFoundState>}

            {!isLoading && client && (
              <>
                <Section>
                  <SectionHeading>Details</SectionHeading>
                  <OverviewTab
                    client={client}
                    onEditClick={() => setActivePanel('edit')}
                    canManage={allowManage}
                  />
                </Section>

                <Section>
                  <SectionHeading>Clinicians</SectionHeading>
                  <CliniciansTab
                    clientId={clientId}
                    canManage={allowManage}
                    supervisingClinicianId={client.supervisingClinicianId}
                    supervisingClinician={client.supervisingClinician}
                  />
                </Section>

                <Section>
                  <SectionHeading>Goals</SectionHeading>
                  <ClientGoalsPanel
                    clientId={clientId}
                    clientName={clientDisplayName}
                    canManage={allowManage}
                    zIndex={nestedZIndex}
                    onOverlayOpenChange={handleGoalsOverlayOpenChange}
                  />
                </Section>

                {client.statusEvents !== undefined && (
                  <Section>
                    <ClientStatusHistorySection clientId={clientId} entries={client.statusEvents} />
                  </Section>
                )}
              </>
            )}
          </Body>
        </Panel>
      </Overlay>

      {/* Nested overlays — rendered outside Overlay so their backdrop clicks don't
          propagate to the drawer's onClose. Each receives zIndex={nestedZIndex} (popover=1060)
          so it stacks above the drawer Panel (modal=1050):
          - Modal-based overlays (EditClientModal, SetOverseeingBcbaModal) portal into
            document.body and pass the zIndex down to <Modal zIndex>.
          - AssignProvidersPanel is position:fixed (no portal) and applies the zIndex
            to its own Overlay ($zIndex-1) and Panel ($zIndex) styled components.
          The drawer's Escape handler reads activePanelRef — when a nested overlay is
          open (activePanel !== null), Escape is a no-op in the drawer. */}
      {client && (
        <>
          <EditClientModal
            isOpen={activePanel === 'edit'}
            onClose={closePanel}
            client={client}
            zIndex={nestedZIndex}
          />

          {activePanel === 'assign' && (
            <AssignProvidersPanel
              clientId={client.id}
              clientName={clientDisplayName}
              onClose={closePanel}
              zIndex={nestedZIndex}
            />
          )}

          <SetOverseeingBcbaModal
            isOpen={activePanel === 'bcba'}
            onClose={closePanel}
            clientId={client.id}
            clientName={clientDisplayName}
            currentSupervisingClinicianId={client.supervisingClinicianId}
            currentSupervisingClinicianName={fullName(client.supervisingClinician)}
            zIndex={nestedZIndex}
          />
        </>
      )}
    </>
  );
}

export default ClientDetailDrawer;
